'use client';

import Image from 'next/image';
import Link from 'next/link';
import { HoverCard, ScrollArea, Table, Text } from '@mantine/core';
import {
  IconCheck,
  IconCornerRightUp,
  IconHourglassEmpty,
  IconLibraryPhoto,
  IconLockFilled,
  IconTransfer,
  IconX,
} from '@tabler/icons-react';
import { periodB } from '@/shared/libs/dates';
import { components } from '@/shared/types/api/TeraApi';
import { useTranslation } from '@/i18n/client';

export function ItemsTable({ items }: { items: components['schemas']['ItemListItemDTO'][] }) {
  const { t, lng } = useTranslation('items');

  const rows = items.map((item, index) => (
    <Table.Tr key={index}>
      <Table.Td style={{ textAlign: 'center', width: '100px' }}>{item.id}</Table.Td>
      <Table.Td w={40} h={40} p={0} style={{ lineHeight: 0 }}>
        <Image src={item.icon} alt='icon' width={40} height={40} />
      </Table.Td>
      <Table.Td>
        <Link
          className={'item_grade_' + item.grade}
          href={`/item/${item.id}`}
          style={{ textDecoration: 'none' }}
        >
          {item.name[lng].length > 0 ? item.name[lng] : 'NO NAME'}
        </Link>
      </Table.Td>
      <Table.Td style={{ textAlign: 'center' }}>{item.level}</Table.Td>
      <Table.Td style={{ textAlign: 'center' }}>
        {item.obtainable ? (
          <IconCheck size={16} style={{ marginTop: '6px' }} color='green' />
        ) : (
          <IconX size={16} style={{ marginTop: '6px' }} color='red' />
        )}
      </Table.Td>
      <Table.Td style={{ textAlign: 'center' }}>
        {item.tradable ? (
          <IconCheck size={16} style={{ marginTop: '6px' }} color='green' />
        ) : (
          <IconX size={16} style={{ marginTop: '6px' }} color='red' />
        )}
      </Table.Td>
      <Table.Td style={{ textAlign: 'center' }}>
        {item.periodByWebAdmin ? (
          <HoverCard shadow='md'>
            <HoverCard.Target>
              <IconHourglassEmpty size={16} style={{ marginTop: '6px' }} color='red' />
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <Text size='sm'>{t('hoverCard.periodAdmin')}</Text>
            </HoverCard.Dropdown>
          </HoverCard>
        ) : item.period ? (
          <HoverCard shadow='md'>
            <HoverCard.Target>
              <IconHourglassEmpty size={16} style={{ marginTop: '6px' }} color='orange' />
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <Text size='sm'>{t('hoverCard.periodSet', { period: periodB(item.period, t) })}</Text>
            </HoverCard.Dropdown>
          </HoverCard>
        ) : (
          <IconX size={16} style={{ marginTop: '6px' }} color='green' />
        )}
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Table.ScrollContainer maxHeight={items.length > 15 ? 650 : undefined} minWidth={340}>
      <Table stickyHeader>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ textAlign: 'center' }}>#</Table.Th>
            <Table.Th style={{ textAlign: 'center' }}>
              <HoverCard shadow='md'>
                <HoverCard.Target>
                  <IconLibraryPhoto size={16} style={{ marginTop: '6px' }} />
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Text size='sm'>{t('hoverCard.icon')}</Text>
                </HoverCard.Dropdown>
              </HoverCard>
            </Table.Th>
            <Table.Th>{t('name')}</Table.Th>
            <Table.Th w={40} style={{ textAlign: 'center' }}>
              <HoverCard shadow='md'>
                <HoverCard.Target>
                  <IconCornerRightUp size={16} style={{ marginTop: '6px' }} />
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Text size='sm'>{t('hoverCard.level')}</Text>
                </HoverCard.Dropdown>
              </HoverCard>
            </Table.Th>
            <Table.Th w={40} style={{ textAlign: 'center' }}>
              <HoverCard shadow='md'>
                <HoverCard.Target>
                  <IconLockFilled size={16} style={{ marginTop: '6px' }} />
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Text size='sm'>{t('hoverCard.obtainable')}</Text>
                </HoverCard.Dropdown>
              </HoverCard>
            </Table.Th>
            <Table.Th w={40} style={{ textAlign: 'center' }}>
              <HoverCard shadow='md'>
                <HoverCard.Target>
                  <IconTransfer size={16} style={{ marginTop: '6px' }} />
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Text size='sm'>{t('hoverCard.tradeable')}</Text>
                </HoverCard.Dropdown>
              </HoverCard>
            </Table.Th>
            <Table.Th w={40} style={{ textAlign: 'center' }}>
              <HoverCard shadow='md'>
                <HoverCard.Target>
                  <IconHourglassEmpty size={16} style={{ marginTop: '6px' }} />
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Text size='sm'>{t('hoverCard.period')}</Text>
                </HoverCard.Dropdown>
              </HoverCard>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

export default ItemsTable;
